/**
 * @module app.cm
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService } from 'app/cm/services/case-service';
import * as CM from 'app/cm/models/essette-api';
import { localSort } from 'app/shared/lib/sorting';


export interface CustomCareTeamParticipantHistory extends CM.CareTeamParticipantHistory {
    name?: string | null;
}

@autoinject()
export class CaseCareTeamHistory extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    protected caseId: string | null = null;

    protected careTeamParticipantHistory: CustomCareTeamParticipantHistory[] = [];

    private apiSorting = new Sorting();
    private localSorting: Sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 10;

    private localSortProperties: string[] = ['name'];

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.apiSorting.entries = [
            {
                reference: 'audit.start.timestamp',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;

        //If LocalSort
        if(this.localSortProperties.indexOf(property) > -1) {
            const { localSorting } = this;
            const careTeamParticipantHistory: CustomCareTeamParticipantHistory[] = <CustomCareTeamParticipantHistory[]>this.careTeamParticipantHistory;

            if(direction === null) {
                localSorting.remove(property);
                if(this.apiSorting.entries.length == 0) {
                    localSorting.add({
                        reference: 'name',
                        direction: SortDirection.Ascending
                    });
                }
            } else {
                localSorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            localSort(careTeamParticipantHistory, localSorting);
        }
        // Else If API Sort
        else {
            const { apiSorting: sorting } = this;
            if(direction === null) {
                sorting.remove(property);
            } else {
                sorting.add({
                    reference: property,
                    direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
                });
            }
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadCaseCareTeamHistory();
    }

    // Gives each participant history (both users and providers) a shared name value to enable local sorting
    private restructureParticipantHistory(): void {
        if(!this.careTeamParticipantHistory.length) return;

        this.careTeamParticipantHistory.forEach(history => history.name = history.participant?.assigned?.toUser?.name?.full || history.participant?.assigned?.toProvider?.name?.full);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadCaseCareTeamHistory(): Promise<void> {
        if(!this.caseId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.apiSorting,
            includeInactive: true
        };

        const includes = [
            'participant.assigned.toUser',
            'participant.assigned.toProvider',
            'participant.relationship',
            'audit.start',
            'audit.start.user',
            'audit.end',
            'audit.end.user'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadCareTeamParticipantHistory(this.caseId, options, includes);
            this.careTeamParticipantHistory = response.results;

            this.restructureParticipantHistory();

            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
